import { useState, useMemo } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import { TC } from "../../tokens/text";
import {
  IoCheckmarkCircleOutline,
  IoRefreshOutline,
  IoBanOutline,
  IoTimeOutline,
  IoWarningOutline,
  IoShieldCheckmarkOutline,
} from "react-icons/io5";

const MOCK_AUDIT = [
  { id: "evt-001", type: "granted",  clientName: "Acme Ltd",            bank: "Barclays", actor: "j.smith@firm.co.uk",   ts: "2026-04-10T14:23:00Z", consentId: "cns-aab112", detail: "90-day consent granted via Open Banking" },
  { id: "evt-002", type: "granted",  clientName: "Maple Consulting",     bank: "NatWest",  actor: "j.smith@firm.co.uk",   ts: "2026-04-11T07:15:00Z", consentId: "cns-eef778", detail: "90-day consent granted via Open Banking" },
  { id: "evt-003", type: "renewed",  clientName: "TechStart Solutions",  bank: "HSBC",     actor: "a.jones@firm.co.uk",   ts: "2026-01-21T18:30:00Z", consentId: "cns-bbc334", detail: "Consent renewed for 90 days" },
  { id: "evt-004", type: "pending",  clientName: "Green Energy Co",      bank: "Monzo",    actor: "a.jones@firm.co.uk",   ts: "2026-04-11T11:00:00Z", consentId: null,          detail: "Consent link sent to client, awaiting authorisation" },
  { id: "evt-005", type: "revoked",  clientName: "Nova Retail Group",    bank: "Lloyds",   actor: "client (self)",        ts: "2026-03-01T10:00:00Z", consentId: "cns-dde556", detail: "Consent revoked by client at bank" },
  { id: "evt-006", type: "expired",  clientName: "Brightside Agency",    bank: "Starling", actor: "system",               ts: "2026-03-15T00:00:00Z", consentId: "cns-ggj990", detail: "Consent reached 90-day expiry; ingestion paused" },
  { id: "evt-007", type: "warning",  clientName: "TechStart Solutions",  bank: "HSBC",     actor: "system",               ts: "2026-04-05T08:00:00Z", consentId: "cns-bbc334", detail: "Consent expiring in 16 days — renewal reminder sent" },
  { id: "evt-008", type: "granted",  clientName: "Acme Ltd",             bank: "Barclays", actor: "j.smith@firm.co.uk",   ts: "2026-01-10T09:00:00Z", consentId: "cns-aaz001", detail: "Initial 90-day consent granted" },
  { id: "evt-009", type: "revoked",  clientName: "Acme Ltd",             bank: "Barclays", actor: "j.smith@firm.co.uk",   ts: "2026-03-20T16:45:00Z", consentId: "cns-aaz001", detail: "Consent revoked by accountant (client off-boarded)" },
];

const EVENT_CONFIG = {
  granted:  { label: "Granted",  color: "#01b574", bg: "rgba(1,181,116,0.12)",  border: "rgba(1,181,116,0.25)",  icon: <IoCheckmarkCircleOutline /> },
  renewed:  { label: "Renewed",  color: TC.linkHover, bg: "rgba(0,117,255,0.12)",  border: "rgba(0,117,255,0.25)",  icon: <IoRefreshOutline /> },
  revoked:  { label: "Revoked",  color: "#f44336", bg: "rgba(245,57,57,0.12)",  border: "rgba(245,57,57,0.25)",  icon: <IoBanOutline /> },
  expired:  { label: "Expired",  color: "#777",    bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.1)", icon: <IoTimeOutline /> },
  pending:  { label: "Pending",  color: "#f5a623", bg: "rgba(245,166,35,0.1)",  border: "rgba(245,166,35,0.25)", icon: <IoTimeOutline /> },
  warning:  { label: "Warning",  color: "#f5a623", bg: "rgba(245,166,35,0.1)",  border: "rgba(245,166,35,0.25)", icon: <IoWarningOutline /> },
};

function fmtDateTime(iso) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const EVENT_FILTERS = ["All", "Granted", "Renewed", "Revoked", "Expired", "Pending", "Warning"];

export default function ConsentAuditLog() {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    let list = [...MOCK_AUDIT].sort((a, b) => new Date(b.ts) - new Date(a.ts));
    if (filter !== "All") list = list.filter((e) => e.type === filter.toLowerCase());
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((e) =>
        e.clientName.toLowerCase().includes(q) ||
        e.bank.toLowerCase().includes(q) ||
        e.actor.toLowerCase().includes(q) ||
        (e.consentId || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [filter, search]);

  const counts = useMemo(() => {
    const map = {};
    MOCK_AUDIT.forEach((e) => { map[e.type] = (map[e.type] || 0) + 1; });
    return map;
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox mb={3}>
          <VuiTypography variant="lg" fontWeight="bold" sx={{ color: TC.primary }}>Consent Audit Log</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
            Full audit trail of all consent events — grants, renewals, revocations, and expirations
          </VuiTypography>
        </VuiBox>

        {/* Summary chips */}
        <Grid container spacing={2} mb={3}>
          {Object.entries(EVENT_CONFIG).map(([type, cfg]) => (
            <Grid item xs={6} sm={4} md={2} key={type}>
              <VuiBox p={1.5} borderRadius="12px" textAlign="center"
                sx={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                <VuiTypography variant="h5" fontWeight="bold" sx={{ color: cfg.color }}>{counts[type] || 0}</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: cfg.color, opacity: 0.8 }}>{cfg.label}</VuiTypography>
              </VuiBox>
            </Grid>
          ))}
        </Grid>

        <Card>
          <VuiBox p={3}>
            {/* Toolbar */}
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
              <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary }}>
                All Events
              </VuiTypography>
              <VuiBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
                <VuiBox display="flex" gap={0.75} flexWrap="wrap">
                  {EVENT_FILTERS.map((f) => (
                    <VuiBox
                      key={f} px={1.5} py={0.5} borderRadius="20px"
                      role="button"
                      tabIndex={0}
                      aria-pressed={filter === f}
                      sx={{
                        cursor: "pointer", fontSize: 11, fontWeight: 600,
                        color: filter === f ? "white" : "rgba(255,255,255,0.45)",
                        background: filter === f ? "rgba(0,117,255,0.3)" : "rgba(255,255,255,0.05)",
                        border: filter === f ? "1px solid rgba(0,117,255,0.5)" : "1px solid rgba(255,255,255,0.08)",
                        transition: "all 0.2s", userSelect: "none",
                      }}
                      onClick={() => setFilter(f)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setFilter(f);
                        }
                      }}
                    >
                      {f}
                    </VuiBox>
                  ))}
                </VuiBox>
                <VuiBox sx={{ minWidth: 200 }}>
                  <VuiInput placeholder="Search client, bank, actor..." value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    inputProps={{ "aria-label": "Search consent audit log" }}
                    icon={{ component: "search", direction: "left" }} />
                </VuiBox>
              </VuiBox>
            </VuiBox>

            {/* Timeline */}
            {filtered.length === 0 ? (
              <VuiTypography variant="button" sx={{ color: TC.primary }}>No events match your filter.</VuiTypography>
            ) : (
              <VuiBox>
                {filtered.map((evt, idx) => {
                  const cfg = EVENT_CONFIG[evt.type] || EVENT_CONFIG.pending;
                  return (
                    <VuiBox key={evt.id} display="flex" gap={2} mb={idx < filtered.length - 1 ? 2 : 0}>
                      {/* Timeline line + icon */}
                      <VuiBox display="flex" flexDirection="column" alignItems="center" width={36} flexShrink={0}>
                        <VuiBox display="flex" alignItems="center" justifyContent="center"
                          width={36} height={36} borderRadius="50%"
                          sx={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color, fontSize: 17, flexShrink: 0 }}>
                          {cfg.icon}
                        </VuiBox>
                        {idx < filtered.length - 1 && (
                          <VuiBox flexGrow={1} width="1px" mt={0.5}
                            sx={{ background: "#f4f8ff", minHeight: 20 }} />
                        )}
                      </VuiBox>

                      {/* Event content */}
                      <VuiBox flexGrow={1} pb={idx < filtered.length - 1 ? 2 : 0}>
                        <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                          <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary }}>{evt.clientName}</VuiTypography>
                            <Chip label={cfg.label} size="small"
                              sx={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontSize: 10, height: 20 }} />
                          </VuiBox>
                          <VuiTypography variant="caption" sx={{ color: TC.secondary,  whiteSpace: "nowrap" }}>
                            {fmtDateTime(evt.ts)}
                          </VuiTypography>
                        </VuiBox>

                        <VuiTypography variant="caption" display="block" mt={0.5} sx={{ color: TC.secondary }}>{evt.detail}</VuiTypography>

                        <VuiBox display="flex" gap={2} mt={0.75} flexWrap="wrap">
                          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                            🏦 <span style={{ color: TC.midtone }}>{evt.bank}</span>
                          </VuiTypography>
                          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                            👤 <span style={{ color: TC.midtone }}>{evt.actor}</span>
                          </VuiTypography>
                          {evt.consentId && (
                            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                              🔑 <span style={{ color: TC.secondary, fontFamily: "monospace" }}>{evt.consentId}</span>
                            </VuiTypography>
                          )}
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  );
                })}
              </VuiBox>
            )}
          </VuiBox>
        </Card>

        <VuiBox mt={2} p={2} borderRadius="12px"
          sx={{ background: "rgba(0,117,255,0.05)", border: "1px solid rgba(0,117,255,0.15)" }}
          display="flex" alignItems="flex-start" gap={1.5}>
          <IoShieldCheckmarkOutline style={{ color: TC.linkHover, fontSize: 20, flexShrink: 0, marginTop: 2 }} />
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
            <strong style={{ color: TC.primary }}>GDPR / PSD2 audit trail: </strong>
            All consent events are immutably logged with timestamp, actor, and consent reference.
            This log is retained for regulatory compliance. Historical data is preserved after revocation or expiry.
          </VuiTypography>
        </VuiBox>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

